import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApiService } from '@api/api.service';
import { AccountSubscriptionStatus, StripePlanRequestPlan } from '@api/types';
import { MessageService } from '@shared/message/message.service';
import { ModalService } from '@shared/modal/modal.service';
import { ContentfulService } from '@shared/services/contentful.service';
import { Subscription } from 'rxjs';
import { AccountService } from '../account.service';

@Component({
  selector: 'rav-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss']
})
export class BillingComponent implements OnInit, OnDestroy {
  cloudPlans: any;
  currentPlan: any;
  isAnnualSwitch = true;
  allFeatures = [];
  planFeatures = [];
  modalSubscription: Subscription;

  constructor(
    private contentful: ContentfulService,
    private api: ApiService,
    public modal: ModalService,
    public message: MessageService,
    public account: AccountService
  ) {}

  async ngOnInit() {
    this.cloudPlans = await this.contentful.getCloudPlans();
    this.planFeatures = this.cloudPlans
      .map((plan) => plan.fields.pricingFeatures)
      .map((features) => features.map((feature) => feature.fields.title));
    this.allFeatures = [].concat.apply([], this.planFeatures);
    await this.loadData();

    this.modalSubscription = this.modal.close.subscribe((onCloseData) => {
      this.loadData();
      if (onCloseData && onCloseData.selectedPlan) this.changeSubscription(onCloseData.selectedPlan);
    });
  }

  ngOnDestroy() {
    if (this.modalSubscription) this.modalSubscription.unsubscribe();
  }

  /**
   * The message to display for the current plan
   */
  planMessage(): string {
    switch (this.account.details.subscription_plan) {
      case 'none':
        return `You're not paying for a subscription plan`;
      case 'free.trial':
        let freeStatus = 'end';
        if (this.account.details.subscription_status === 'scheduledToUpgrade') freeStatus = 'upgrade';
        if (this.account.details.subscription_status === 'scheduledToCancel') freeStatus = 'cancel';
        return `Your trial will ${freeStatus} on ${formatDate(this.account.details.trial_ends_at, 'longDate', 'en')}`;
      default:
        let defaultStatus = 'active';
        if (this.account.details.subscription_status === 'scheduledToUpgrade') defaultStatus = 'schedule to upgrade';
        if (this.account.details.subscription_status === 'scheduledToCancel') defaultStatus = 'scheduled to cancel';
        return `Your subscription is ${defaultStatus}`;
    }
  }

  /**
   * Determines if the current plan can be cancelled
   */
  canCancel(): boolean {
    return (
      this.account.details.subscription_status === AccountSubscriptionStatus.ScheduledToUpgrade ||
      this.account.details.subscription_status === AccountSubscriptionStatus.Subscribed
    );
  }

  /**
   * Determines if the current plan can be resumed
   */
  canResume(): boolean {
    return this.account.details.subscription_status === AccountSubscriptionStatus.ScheduledToCancel;
  }

  /**
   * Changes the subscription to the selected plan
   *
   * @param selectedPlan The selected plan to change to
   */
  async changeSubscription(selectedPlan: StripePlanRequestPlan) {
    if (this.account.details.card_last_four) {
      try {
        await this.api.accounts.plans.post({ plan: selectedPlan }).toPromise();
      } catch (e) {
        this.message.show('billing-plan', {
          message: (e.error && e.error.message) || 'An unknown error occurred, please try again or contact support',
          type: 'alert'
        });
        return;
      }
      this.message.show('billing-plan', { message: 'Your plan was changed successfully', type: 'success' });
      await this.loadData();
    } else {
      this.modal.open.next({ modalId: 'payment', selectedPlan: selectedPlan });
    }
  }

  /**
   * Cancels the current plan
   */
  async cancelSubscription() {
    if (!confirm('Are you sure you want to cancel your account?')) return;
    await this.api.accounts.plans.delete().toPromise();
    this.message.show('billing-plan', { message: 'Your plan was cancelled successfully', type: 'success' });
    await this.loadData();
  }

  /**
   * Resumes the subscription
   */
  async resumeSubscription() {
    await this.api.accounts.plans.resume.post().toPromise();
    this.message.show('billing-plan', { message: 'Your plan has been resumed', type: 'success' });
    await this.loadData();
  }

  /**
   * Loads the data, includes getting the account details and sets the current plan
   */
  private async loadData() {
    await this.account.reloadDetails();
    this.currentPlan = this.cloudPlans.filter((plan) => plan.fields.stripePlanName === this.account.details.type)[0];
  }
}
